import { Note } from "./Note.js";

export interface MusicXmlUtilLoadOptions {
    transpose?: number;
    label?: string;
}

export interface MusicXmlUtilOptions {
}

export class MusicXmlUtil {

    private _instruments: {
        sign: string;
        line: number;
        name: string;
    }[] = [
            {
                sign: "G",
                line: 2,
                name: "Violin 1"
            },
            {
                sign: "G",
                line: 2,
                name: "Violin 2"
            },
            // {
            //     sign: "G",
            //     line: 2,
            //     name: "Pizzicato Strings"
            // },
            {
                sign: "C",
                line: 3,
                name: "Viola"
            },
            {
                sign: "F",
                line: 4,
                name: "Cello"
            }
        ];
    private _parser: DOMParser;
    private _serializer: XMLSerializer;

    constructor(options: MusicXmlUtilOptions) {
        this._parser = new DOMParser();
        this._serializer = new XMLSerializer();
    }

    public async normalize(src: string, debugName: string) {

        const text = src.startsWith("http") ? await e5.load("text", src) : src;

        const xmlDoc = this._parser.parseFromString(text, "text/xml") as XMLDocument;

        const parts = xmlDoc.querySelectorAll("part");
        if (parts.length != this._instruments.length) {
            throw new Error(`Invalid number of parts[${parts.length}]/instruments[${this._instruments.length}] in MusicXML file. ${debugName}`);
        }

        xmlDoc.querySelectorAll("direction").forEach((e) => {
            e.remove();
            // console.warn(`Removed direction label from MusicXML file. ${debugName}`);
        });

        xmlDoc.querySelectorAll("bar-style")
            .forEach((e) => {
                e.textContent = "light-light";//light-heavy
            });

        parts.forEach((e, partIndex) => {

            const clef = e.querySelector("clef");
            if (!clef) {
                throw new Error(`Missing clef XML node in MusicXML file. ${debugName}`);
            }

            const clefSign = clef.querySelector(":scope > sign");
            if (!clefSign) {
                throw new Error(`Missing clef > sign XML node in MusicXML file. ${debugName}`);
            }

            const clefLine = clef.querySelector(":scope > line");
            if (!clefLine) {
                throw new Error(`Missing clef > line XML node in MusicXML file. ${debugName}`);
            }

            if (clefSign.textContent.trim() != this._instruments[partIndex].sign) {
                console.warn(`Wrong clef sign "${clefSign.textContent.trim()}" for instrument "${partIndex + 1}".`
                    + ` Expected "${this._instruments[partIndex].sign}". ${debugName}`);
                clefSign.textContent = this._instruments[partIndex].sign;
            }

            if (clefLine.textContent.trim() != this._instruments[partIndex].line + "") {
                console.warn(`Wrong clef line "${clefLine.textContent.trim()}" for instrument "${partIndex + 1}".`
                    + ` Expected "${this._instruments[partIndex].line}". ${debugName}`);
                clefLine.textContent = this._instruments[partIndex].line + "";
            }

            // console.log(context, this._clefs[partIndex]);

            // this._clefs[partIndex]

        });

        // return xmlDoc;

        return this.serializeXml(xmlDoc);
    }

    public async load(src: string, options: MusicXmlUtilLoadOptions = {}) {
        options = e5.object.merge({
            transpose: 0,
            label: ""
        }, options);

        const text = src.startsWith("http") ? await e5.load("text", src) : src;
        const xmlDoc = this._parser.parseFromString(text, "text/xml") as XMLDocument;


        //     <direction placement="above">
        //     <direction-type>
        //       <metronome parentheses="no">
        //         <beat-unit>quarter</beat-unit>
        //         <per-minute>120</per-minute>
        //       </metronome>
        //     </direction-type>
        //     <sound tempo="120"/>
        //   </direction>


        if (options.label) {
            const firstPart = xmlDoc.querySelector("score-partwise>part>measure");
            if (firstPart) {
                firstPart.innerHTML = `
                <direction placement="above">
                <direction-type>
                <words relative-x="200">${options.label}</words>
                </direction-type>
                </direction>
                ` + firstPart.innerHTML;
            }
        }

        xmlDoc.querySelectorAll("bar-style")
            .forEach((e) => {
                e.textContent = "light-light";//light-heavy
            });

        return xmlDoc;

        // return this.serializeXml(xmlDoc);
    }

    public async fromMidiUrl(url: string, name: string): Promise<string> {
        return this.fromMidi(await MidiLoader.fromUrl(url), name);
    }

    private beatsToType(beats: number):
        | "whole"     // 4 beats
        | "half"    // 2 beats
        | "quarter" // 1 beat
        | "eighth"  // 1/2 beat
        | "16th"    // 1/4 beat 
    {
        if (beats <= 0.25) {
            return "16th";
        }
        else if (beats <= 0.5) {
            return "eighth";
        }
        else if (beats <= 1) {
            return "quarter";
        }
        else if (beats <= 2) {
            return "half";
        }
        else if (beats <= 4) {
            return "whole";
        }
        return "whole";
    }

    private fillRest(beatsAvailable: number, beatsToDuration: number): string[] {
        let beatsFiller = [
            4,//whole
            2,//half
            1,//quarter
            0.5,//eighth
            0.25,//16th
        ];

        let sMeasures: string[] = [];

        loop:
        while (beatsAvailable >= 0.25) {
            for (let b of beatsFiller) {
                if (beatsAvailable >= b) {
                    let type = this.beatsToType(b);
                    beatsAvailable -= b;
                    sMeasures.push(`
                    <note>
                        <rest measure="yes"/>
                        <duration>${(b * beatsToDuration).toFixed(4)}</duration>
                        <type>${type}</type>
                    </note>
                    `);
                    continue loop;
                }
            }

            return sMeasures;
        }

        return sMeasures;
    }

    public fromMidi(midi: MidiFormat, name: string): string {

        let xml = `<?xml version="1.0" encoding="UTF-8"?>
        <!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 4.0 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">

        <score-partwise version="4.0">

            <identification>
                <encoding>
                    <software>eventfive</software>
                    <encoding-date>2023-03-17</encoding-date>
                    <supports element="accidental" type="yes"/>
                    <supports element="beam" type="yes"/>
                    <supports element="stem" type="yes"/>
                </encoding>
            </identification>
        `;

        // console.log(JSON.stringify(midi, null, "  "));

        const numerator = 4;//midi.header.timeSignatures[0]?.timeSignature[0];
        const denominator = 4;//midi.header.timeSignatures[0]?.timeSignature[1];

        //pulses per quarter note
        const ppq = midi.header.ppq;

        const ticks16th = ppq / 4;
        // console.log("ppq / 4 = 16th", ppq / 4);

        const bpm = midi.header.tempos[0].bpm;
        const tempo = Math.round(bpm).toString();//(100).toString();//
        const ticks = midi.durationTicks;
        const beats = (midi.duration / 60) * bpm;
        const beatsToTicks = ticks / beats;
        const ticksPerMeasure = numerator * ppq;
        const ticksToBeats = beats / ticks;


        /**
         * Prepare
         */
        for (let track of midi.tracks) {
            if (!track.notes) {
                track.notes = [];
            }
            for (const note of track.notes) {
                // note.ticks = e5.Calc.roundTo(note.ticks, ticks16th);
                // note.durationTicks = e5.Calc.roundTo(note.durationTicks, ticks16th);
            }
        }

        let scoreParts = ``;
        let parts = ``;

        /**
         * fifths | Key signature
         */
        let fifths: number = null;
        if (fifths === null) {
            if (midi.header.keySignatures[0]?.key) {
                fifths = Note.getNumberOfFifths(midi.header.keySignatures[0].key);
            }
        }
        if (fifths === null) {
            const allNotes: string[] = [];
            for (let track of midi.tracks) {
                for (const note of track.notes) {
                    allNotes.push(note.name);
                }
            }
            const info = Note.identifyKeySignature(allNotes);
            if (info) {
                fifths = info.fifths;
            }
        }
        if (fifths === null) {
            fifths = 0;
        }

        /**
         * mode | Key signature
         */
        let mode: string = null;
        if (mode === null) {
            if (midi.header.keySignatures[0]?.scale) {
                mode = midi.header.keySignatures[0]?.scale;
            }
        }
        if (mode === null) {
            const allNotes: string[] = [];
            for (let track of midi.tracks) {
                for (const note of track.notes) {
                    allNotes.push(note.name);
                }
            }
            const info = Note.identifyKeySignature(allNotes);
            if (info) {
                mode = info.mode;
            }
        }
        if (mode === null) {
            mode = "none";
        }

        let trackNumber = 0;
        for (let track of midi.tracks) {
            const id = "P" + (++trackNumber);

            scoreParts += `
                <score-part id="${id}">
                    <part-name>Violin 1</part-name>
                    <part-abbreviation>Vl. 1</part-abbreviation>
                    <score-instrument id="${id}-I1">
                        <instrument-name>Violine</instrument-name>
                    </score-instrument>
                </score-part>
                `;

            /**
             * Split into measures
             */
            interface NoteInfo {
                raw: MidiTrackNote;
                type: string;
                durationTicks: number;
                durationBeats: number;
                index: number;
                beam?: {
                    number: number;
                    state: "begin" | "continue" | "end";
                    index: number;
                }
            }
            const measures: {
                index: number;
                startTick: number;
                endTick: number;
                notes: NoteInfo[];
            }[] = [];
            const maxMeasureCount = Math.ceil(ticks / ticksPerMeasure);
            for (let i = 0; i < maxMeasureCount; ++i) {
                measures.push({
                    index: measures.length,
                    startTick: i * ticksPerMeasure,
                    endTick: (i + 1) * ticksPerMeasure,
                    notes: [],
                });
            }


            for (let note of track.notes) {
                const ticks = e5.Calc.roundTo(note.ticks, ticks16th);
                const nBpm = ticksToBeats * ticks;
                const measureCount = Math.floor(nBpm / denominator);
                //TODO: check...
                if (!measures[measureCount]) {
                    console.log("No Measure at index:", measureCount, name);
                    continue;
                }
                measures[measureCount].notes.push(
                    {
                        index: measures[measureCount].notes.length,
                        raw: note,
                        /**
                         * See below: using division equal to ppq will work :)
                         */
                        type: this.beatsToType(note.durationTicks * ticksToBeats),
                        durationBeats: note.durationTicks * ticksToBeats,//e5.Calc.roundTo(note.durationTicks * ticksToBeats, 0.25),
                        durationTicks: e5.Calc.roundTo(note.durationTicks, ticks16th)
                    }
                );
            }

            /**
             * Connect beams
             * @see https://www.musicnotes.com/now/musictheory/note-beaming-and-grouping-in-music-theory/
             */
            for (let m of measures) {
                let lastInfo: NoteInfo = null;
                for (let info of m.notes) {
                    if (lastInfo) {
                        if (lastInfo.type === info.type
                            && info.durationBeats < 0.5
                            && (info.raw.ticks - lastInfo.raw.ticks) * ticksToBeats < 1
                        ) {
                            if (lastInfo.beam) {
                                if (lastInfo.beam.state === "end") {
                                    if (info.index < m.notes.length - 1) {
                                        info.beam = {
                                            index: 0,
                                            number: 1,
                                            state: "begin"
                                        };
                                    }
                                } else {
                                    let isEnd = lastInfo.beam.index > 0 || info.index === m.notes.length - 1;
                                    info.beam = {
                                        index: isEnd ? 0 : lastInfo.beam.index + 1,
                                        number: lastInfo.beam.number,
                                        state: isEnd ? "end" : "continue"
                                    };
                                }
                            } else {
                                lastInfo.beam = {
                                    index: 0,
                                    number: 1,
                                    state: "begin"
                                };

                                info.beam = {
                                    index: 1,
                                    number: 1,
                                    state: info.index === m.notes.length - 1 ? "end" : "continue"
                                };
                            }
                        } else if (lastInfo.beam) {
                            lastInfo.beam.state = "end";
                        }
                    }
                    lastInfo = info;
                }
            }

            let sMeasures = "";
            for (let m of measures) {

                sMeasures += `<measure number="${(m.index + 1)}">`;

                if (m.index == 0) {

                    let clefData = this._instruments[trackNumber - 1];
                    let clef = `
                    <clef>
                        <sign>${clefData.sign}</sign>
                        <line>${clefData.line}</line>
                    </clef>
                    `;

                    sMeasures += `
                    <attributes>
                        <divisions>${(ppq).toFixed(4)}</divisions>
                        <key>
                            <fifths>${fifths}</fifths>
                            <mode>${mode}</mode>
                        </key>
                        <time>
                            <beats>${midi.header.timeSignatures[0].timeSignature[0]}</beats>
                            <beat-type>${midi.header.timeSignatures[0].timeSignature[1]}</beat-type>
                        </time>
                        ${clef}
                    </attributes>
                    `;

                    // if (trackNumber === 1) {
                    //     sMeasures += `
                    //     <direction placement="above">
                    //         <direction-type>
                    //         <metronome parentheses="no">
                    //             <beat-unit>quarter</beat-unit>
                    //             <per-minute>${tempo}</per-minute>
                    //         </metronome>
                    //         </direction-type>
                    //         <sound tempo="${tempo}"/>
                    //     </direction>
                    //     `;
                    // }
                }

                let lastNoteEndTicks = 0;
                let lastNoteStartTicks = 0;

                let sNotes = ``;
                for (let info of m.notes) {

                    const note = info.raw;

                    const nTicks = note.ticks;

                    const isChord = info.index > 0 && Math.abs(nTicks - lastNoteStartTicks) < 0.05;

                    if (info.index > 0) {
                        const deltaTicks = nTicks - lastNoteEndTicks;
                        const deltaBeats = deltaTicks * ticksToBeats;
                        if (deltaBeats > 0.24) {
                            sNotes += this.fillRest(deltaBeats, beatsToTicks);
                        }
                    }

                    lastNoteStartTicks = nTicks;
                    lastNoteEndTicks = nTicks + note.durationTicks;

                    // let stem: "down" | "up" = note.octave <= 4 ? "up" : "down";
                    // let stem: "down" | "up" = ["F", "A", "C", "D"].includes(note.pitch) ? "down" : "up";
                    // <stem>${stem}</stem>

                    let beam = ``;
                    if (info.beam) {
                        beam = `<beam number="${info.beam.number}">${info.beam.state}</beam>`;
                    }


                    const noteName = note.name;//Note.transpose(note.name, 2);

                    sNotes += `
                    <note dynamics="${(note.velocity * 100).toFixed(2)}">
                        ${isChord ? `<chord/>` : ``}
                        <pitch>
                            <step>${Note.getStep(noteName)}</step>
                            <alter>${Note.getAlter(noteName)}</alter>
                            <octave>${Note.getOctave(noteName)}</octave>
                        </pitch>
                        <duration>${(info.durationTicks).toFixed(4)}</duration>
                        <type>${info.type}</type>
                        ${beam}
                    </note>
                    `;
                }

                /**
                 * Fill "rest"
                 */
                let beforeNotes = "";
                let afterNotes = "";
                if (m.notes.length > 0) {
                    let firstNote = m.notes[0];
                    let beforeBeats = (firstNote.raw.ticks - m.startTick) * ticksToBeats;
                    beforeNotes = this.fillRest(beforeBeats, beatsToTicks).join();

                    let lastNote = m.notes[m.notes.length - 1];
                    let afterBeats = (m.endTick - (lastNote.raw.ticks + lastNote.raw.durationTicks)) * ticksToBeats;
                    afterNotes = this.fillRest(afterBeats, beatsToTicks).reverse().join();
                } else {
                    afterNotes = this.fillRest(ticksPerMeasure * ticksToBeats, beatsToTicks).join();
                }

                sMeasures += `
                ${beforeNotes}
                ${sNotes}
                ${afterNotes}
                `;

                if (m.index === measures.length - 1) {
                    sMeasures += `
                    <barline location="right">
                        <bar-style>light-heavy</bar-style>
                    </barline>`;
                }

                sMeasures += `</measure>`;
            }

            parts += `
            <part id="${id}">
                ${sMeasures}
            </part>`;
        }

        xml += `
            <part-list>
                <part-group type="start" number="1">
                    <group-symbol>bracket</group-symbol>
                </part-group>
                ${scoreParts}
            </part-list>
            ${parts}
        </score-partwise>
        `;

        // console.log(xml);

        return xml;
    }

    public serializeXml(xmlDoc: XMLDocument): string {
        return `<?xml version="1.0" encoding="UTF-8"?>\n`
            + this._serializer.serializeToString(xmlDoc.documentElement);
    }






























    private transposeNotes(xmlDoc: XMLDocument, transpose: number) {
        if (transpose) {
            /**
             * @see https://gist.github.com/leo6104/85869c82485da716d7274a685d9cb5dc
             */
            xmlDoc.querySelectorAll("pitch")
                .forEach((p) => {

                    let step = p.querySelector("step")?.textContent;
                    let octave = p.querySelector("octave")?.textContent;
                    let alter = parseInt(p.querySelector("alter")?.textContent || "0");

                    if (!step
                        || !octave) {
                        return;
                    }

                    let name = Note.fromPitch(step, alter, octave);
                    let r = Note.transpose(name, transpose);

                    // console.log(name, "=>", r);

                    // console.log("IN", {
                    //     step,
                    //     octave,
                    //     alter
                    // });

                    // console.log("OUT", {
                    //     step:Note.getStep(r),
                    //     octave:Note.getOctave(r),
                    //     alter:Note.getAlter(r)
                    // });

                    p.querySelector("step").textContent = Note.getStep(r) + "";
                    p.querySelector("octave").textContent = Note.getOctave(r) + "";

                    alter = Note.getAlter(r);
                    if (alter) {
                        if (p.querySelector("alter")) {
                            p.querySelector("alter").textContent = alter + "";
                        } else {
                            let eAlter = xmlDoc.createElement("alter");
                            eAlter.textContent = alter + "";
                            p.append(eAlter);
                        }
                    } else {
                        if (p.querySelector("alter")) {
                            p.querySelector("alter").remove();
                        }
                    }
                });
        }
    }

    private merge(xmlDocs: XMLDocument[]): XMLDocument {
        let t = xmlDocs[0];
        let tParts = t.querySelectorAll("score-partwise>part");

        for (let i = 1; i < xmlDocs.length; ++i) {
            let s = xmlDocs[i];

            s.querySelectorAll("measure>attributes")
                .forEach((e) => {
                    e.remove();
                });

            let sParts = s.querySelectorAll("score-partwise>part");
            if (tParts.length != sParts.length) {
                console.warn("Target parts lenght does not match source part lengths.", tParts.length, "!=", sParts.length);
                continue;
            }

            for (let j = 0; j < tParts.length; ++j) {

                let tPart = tParts[j];
                let sPart = sParts[j];

                tPart.innerHTML += sPart.innerHTML;
            }
        }

        return t;
    }

}